import React from 'react'

import { WebViewActionStrip } from '@acorns/web-components'
import styled from 'styled-components'

import Spinner from 'components/Spinner'
import { withTrackClicked } from 'components/trackers'
import {
  CenteredContentContainer,
  ExternalButtonLink,
} from 'pages/spend/direct-deposit/SharedComponents'
import {
  Bold,
  Container,
  CopyInput,
  EmployerInfo,
  Field,
  FieldLabel,
  FieldRow,
  FieldValue,
  InstructionStep,
  InstructionsList,
  Panel,
  PanelTitle,
  PanelTitleText,
  ProgressBar,
  Title,
  TrackedTitleLink,
} from 'pages/spend/direct-deposit/WebViewComponents'
import { PageAnalytics } from 'utils/analytics'
import { EDIT_DIRECT_DEPOSIT_BUTTON, SPINNER } from 'utils/testing/testIDs'

import { WVProps } from '../OnlineComponents'
import enhance from './enhance'

const EmployerCTA = styled(ExternalButtonLink)`
  display: flex;
  margin: 0 20px;
  justify-content: center;
  position: sticky;
  bottom: 30px;
`

const GoToEmployerLink = withTrackClicked<any, any>((props) => ({
  object_name: 'directDepositOnlineEmployerPortal',
  screen: 'directDepositOnline',
  screen_name: 'directDepositOnline',
  cta_title: 'Go To Employer Portal',
  url: props.ctaUrl,
  employer_name: props.employerName,
  click_switch_target_id: props.clickSwitchTargetId,
}))(EmployerCTA)

export const Page: React.SFC<WVProps> = (props: WVProps) => (
  <>
    {!props.data.loading && <PageAnalytics />}

    {props.data.loading && (
      <CenteredContentContainer data-testid={SPINNER}>
        <Spinner />
      </CenteredContentContainer>
    )}

    {!props.data.loading && (
      <Container>
        <WebViewActionStrip
          title={'Employer Information'}
          action={{
            onClick: props.handleWebViewBack,
            shouldShowCloseIcon: false,
          }}
          link={{ onClick: props.handleWebViewExit, text: 'Cancel' }}
        />

        <ProgressBar progress={50} />

        <PanelTitle>
          <PanelTitleText>Instructions</PanelTitleText>
        </PanelTitle>
        <Panel>
          <Title>How to Set Up Direct Deposit</Title>
          To automatically add your paychecks to Acorns Spend, follow these
          steps:
          <InstructionsList>
            <InstructionStep>Log into your employer portal.</InstructionStep>
            <InstructionStep>
              Find the <Bold>Direct Deposit</Bold> section.
            </InstructionStep>
            <InstructionStep>
              <Bold>Follow the instructions</Bold> using your Acorns Spend
              account information below. When entering your account &amp;
              routing number, it will show as Lincoln Savings Bank, our bank
              partner.
            </InstructionStep>
          </InstructionsList>
        </Panel>

        <PanelTitle>
          <PanelTitleText>Acorns Spend Account Info</PanelTitleText>
        </PanelTitle>
        <Panel>
          <FieldRow>
            <Field>
              <FieldLabel>Account Name</FieldLabel>
              <FieldValue>Acorns Spend Account (Checking)</FieldValue>
              <FieldLabel>
                This will show as Lincoln Savings Bank, our bank partner.
              </FieldLabel>
            </Field>
          </FieldRow>
          <FieldRow>
            <Field>
              <FieldLabel>Routing Number</FieldLabel>
              <CopyInput text={props.routingNumber} />
            </Field>
            <Field>
              <FieldLabel>Account Number</FieldLabel>
              <CopyInput text={props.accountNumber} />
            </Field>
          </FieldRow>
        </Panel>

        <PanelTitle>
          <PanelTitleText>Employer</PanelTitleText>
          <TrackedTitleLink
            onClick={props.handleEdit}
            employerName={props.employerName}
            data-testid={EDIT_DIRECT_DEPOSIT_BUTTON}
          >
            Not your employer?
          </TrackedTitleLink>
        </PanelTitle>
        <Panel>
          <FieldRow>
            <Field>
              <FieldValue>{props.employerName}</FieldValue>
            </Field>
          </FieldRow>
        </Panel>

        <EmployerInfo />

        <GoToEmployerLink
          onPress={props.handleGoToEmployer}
          employerName={props.employerName}
          ctaUrl={props.ctaUrl}
          href={props.ctaUrl}
          target={'_blank'}
          clickSwitchTargetId={props.clickSwitchTargetId}
        >
          {props.processing ? (
            <Spinner height={40} width={40} />
          ) : (
            'Go To Employer Portal'
          )}
        </GoToEmployerLink>
      </Container>
    )}
  </>
)

export default enhance(Page)
